// @ts-nocheck
import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TextEditorStyle } from '@way/theme-host/shared/components/text-editor/text-editor.component';

export interface InfoLayout2Data {
  title?: string;
  description?: string;
  buttonText?: string;
  buttonLink?: string;
  image?: {
    src?: string;
    alt?: string;
    width?: string;
    height?: string;
    scaletype?: 'fill' | 'fit' | 'stretch' | 'cover' | 'contain';
  };
  imageUrl?: string;
  backgroundColor?: string;
  titleStyle?: TextEditorStyle;
}

/**
 * Preview component for the "Info Layout 2" template used in the website builder. Renders heading, paragraph, image and CTA(s) with configurable colors, fonts and title styles; emits an event when a button is clicked.
 * @component
 * @selector app-info-layout-2-preview
 * @example
 *   <app-info-layout-2-preview [data]="sampleData" [headingColor]="'#222'" (buttonClick)="handleButtonClick($event)"></app-info-layout-2-preview>
 * @input InfoLayout2Data | null data - Data model for the layout (text, image, buttons, scale type, etc.). Used to populate heading, paragraph and image.
 * @input string | null headingColor - CSS color string to override the heading color.
 * @input string | null paragraphColor - CSS color string to override the paragraph/body text color.
 * @input string | null headingFont - Font-family or font identifier used for the heading text.
 * @input string | null bodyFont - Font-family or font identifier used for body/paragraph text.
 * @input string | null primaryColor - Primary/accent color used for CTAs or accents in the layout.
 * @input any titleStyles - Inline style object applied to the title element for custom styling (defaults to an empty object).
 * @output EventEmitter<{ link: string; event: Event }> buttonClick - Emitted when a CTA/button is activated. Payload contains the link string and the original DOM event.
 *
 * Key methods:
 * - onButtonClick(link: string | undefined, event: Event): void
 *     Emits the buttonClick event only when a valid link is provided. Intended to be wired to CTA/button click handlers.
 * - getImageSrc(): string
 *     Resolves the image source by preferring data.image.src, then data.imageUrl, returning an empty string if none found.
 * - getImageAlt(): string
 *     Resolves the image alt text from the data model or returns a sensible default ("Car Detailing").
 * - getImageObjectFit(): string
 *     Maps the data.image.scaletype (API value) to a CSS object-fit value ("fill", "contain", "cover", etc.), defaulting to "cover".
 *
 * Lifecycle hooks:
 * - This is a lightweight, presentational standalone component and does not implement Angular lifecycle hooks (OnInit, OnDestroy, etc.) itself — it relies on inputs for reactive rendering.
 */
@Component({
  selector: 'app-info-layout-2-preview',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './info-layout-2.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./info-layout-2.component.scss'],
})
export class InfoLayout2PreviewComponent {
  @Input() data: InfoLayout2Data | null = null;
  @Input() headingColor: string | null = null;
  @Input() paragraphColor: string | null = null;
  @Input() headingFont: string | null = null;
  @Input() bodyFont: string | null = null;
  @Input() primaryColor: string | null = null;
  @Input() titleStyles: any = {};
  @Output() buttonClick = new EventEmitter<{ link: string; event: Event }>();

  onButtonClick(link: string | undefined, event: Event): void {
    if (link) {
      this.buttonClick.emit({ link, event });
    }
  }

  getImageSrc(): string {
    return this.data?.image?.src || this.data?.imageUrl || '';
  }

  getImageAlt(): string {
    return this.data?.image?.alt || 'Car Detailing';
  }

  /**
   * Map the component's image scaletype to a CSS object-fit value.
   * @example
   * etImageObjectFit()
   * 'cover'
   * @returns {string} A CSS object-fit value ('fill', 'contain', or 'cover').
   */
  getImageObjectFit(): string {
    const scaletype = this.data?.image?.scaletype || 'cover';
    // Map API scaletype to CSS object-fit
    switch (scaletype) {
      case 'fill':
      case 'stretch':
        return 'fill';
      case 'fit':
        return 'contain';
      case 'cover':
        return 'cover';
      case 'contain':
        return 'contain';
      default:
        return 'cover';
    }
  }
}
